import React, { useState, useEffect } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useTranslation } from 'react-i18next';
import { Box, Button, Paper, Typography, useMediaQuery, useTheme } from '@mui/material';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const GraficaGeneradores = ({ data = [] }) => {
  const [darkMode, setDarkMode] = useState(false);
  const { t } = useTranslation("global");
  const [orden, setOrden] = useState('original');
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  
  useEffect(() => {
    const checkDarkMode = () => {
      setDarkMode(document.body.classList.contains('dark-mode'));
    };
    
    checkDarkMode();
    
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    
    return () => observer.disconnect();
  }, []);

  const datosValidos = Array.isArray(data) ? data : [];
  const tieneDatos = datosValidos.length > 0 && datosValidos[0]?.limitsResult?.limGeneradores;

  const generatorNames = tieneDatos 
    ? Object.keys(datosValidos[0].limitsResult.limGeneradores)
    : [];

  const [generadorSeleccionado, setGeneradorSeleccionado] = useState(
    generatorNames[0] || null
  );

  const obtenerNombresPeriodo = () => {
    if (!tieneDatos) return [];
    return datosValidos.map(caso => {
      if (caso.caseResult?.[0]?.caseName) {
        return caso.caseResult[0].caseName;
      }
      return `P${datosValidos.indexOf(caso).toString().padStart(2, '0')}`;
    });
  };

  const tieneDatosValidosCompletos = (caso) => {
    return caso?.limitsResult?.limGeneradores && 
           generadorSeleccionado && 
           caso.limitsResult.limGeneradores[generadorSeleccionado];
  };

  const obtenerDatoSeguro = (caso, generador, indice) => {
    try {
      const valor = caso?.limitsResult?.limGeneradores?.[generador]?.[indice] ?? null;
      return typeof valor === 'number' ? parseFloat(valor.toFixed(1)) : null;
    } catch (e) {
      return null;
    }
  };

  const ordenarDatos = (datos, generador, tipoOrden) => {
    if (!tieneDatos || !generador) return { labels: [], datasets: [] };

    const casosConDatos = datosValidos
      .filter(caso => tieneDatosValidosCompletos(caso))
      .map((caso, index) => ({
        caso,
        periodo: obtenerNombresPeriodo()[index],
        valor: obtenerDatoSeguro(caso, generador, 0)
      }));

    let datosOrdenados = [...casosConDatos];
    
    switch (tipoOrden) {
      case 'alfabetico':
        datosOrdenados.sort((a, b) => a.periodo.localeCompare(b.periodo));
        break;
      case 'valor':
        datosOrdenados.sort((a, b) => b.valor - a.valor);
        break;
      case 'original':
      default:
        break;
    }

    return {
      labels: datosOrdenados.map(item => item.periodo),
      datasets: [
        {
          data: datosOrdenados.map(item => item.valor)
        }
      ]
    };
  };

  const prepararDatosGrafica = (generador) => {
    const baseColors = darkMode ? {
      limite: 'rgb(179, 136, 255)', 
      pmax: 'rgb(255, 138, 138)',   
    } : {
      limite: 'rgb(128, 0, 128)',   
      pmax: 'rgb(255, 99, 132)',    
    };

    if (!tieneDatos || !generador) {
      return {
        labels: [],
        datasets: [
          {
            label: t("graphs.limitGeneration"),
            data: [],
            borderColor: baseColors.limite,
            backgroundColor: darkMode ? 'rgba(179, 136, 255, 0.2)' : 'rgba(128, 0, 128, 0.2)',
            borderWidth: 2
          },
          {
            label: 'Pmax',
            data: [],
            borderColor: baseColors.pmax,
            backgroundColor: darkMode ? 'rgba(255, 138, 138, 0.1)' : 'rgba(255, 99, 132, 0.1)',
            borderDash: [5, 5],
            borderWidth: 2,
            pointRadius: 0
          }
        ]
      };
    }

    const casosConDatos = datosValidos.filter(caso => 
      tieneDatosValidosCompletos(caso)
    );
    
    const casosConProblemas = datosValidos.filter(caso => 
      !tieneDatosValidosCompletos(caso)
    );

    if (casosConProblemas.length > 0) {
      console.warn(`Hay ${casosConProblemas.length} casos con datos incompletos o errores`);
    }

    const datosOrdenados = ordenarDatos(datosValidos, generador, orden);

    return {
      labels: datosOrdenados.labels,
      datasets: [
        {
          label: t("graphs.limitGeneration"),
          data: datosOrdenados.datasets[0].data,
          borderColor: baseColors.limite,
          backgroundColor: darkMode ? 'rgba(179, 136, 255, 0.2)' : 'rgba(128, 0, 128, 0.2)',
          tension: 0.1,
          borderWidth: 2,
          pointBackgroundColor: '#ff6a13',
          pointStyle: 'circle',
          pointRadius: isMobile ? 3 : 5,
        },
        {
          label: 'Pmax',
          data: casosConDatos.map(() => 
            obtenerDatoSeguro(casosConDatos[0], generador, 1)
          ),
          borderColor: baseColors.pmax,
          backgroundColor: darkMode ? 'rgba(255, 138, 138, 0.1)' : 'rgba(255, 99, 132, 0.1)',
          borderDash: [5, 5],
          borderWidth: 2,
          pointRadius: 0
        }
      ]
    };
  };

  const opcionesGrafica = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: isMobile ? 'bottom' : 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          },
          color: darkMode ? '#e0e0e0' : '#666'
        }
      },
      title: {
        display: true,
        text: generadorSeleccionado 
          ? `${t("graphs.operationalLimits")} ${generadorSeleccionado.replace('g', '')}`
          : t("graphs.operationalLimits"),
        font: {
          size: isMobile ? 14 : 16
        },
        color: darkMode ? '#e0e0e0' : '#333'
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const value = typeof context.raw === 'number' ? context.raw.toFixed(1) : context.raw;
            return `${context.dataset.label}: ${value} MW`;
          }
        },
        backgroundColor: darkMode ? '#333' : '#fff',
        titleColor: darkMode ? '#e0e0e0' : '#333',
        bodyColor: darkMode ? '#e0e0e0' : '#666',
        borderColor: darkMode ? '#444' : '#ddd',
        bodyFont: {
          size: isMobile ? 10 : 12
        },
        titleFont: {
          size: isMobile ? 12 : 14
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxRotation: isMobile ? 45 : 90,
          minRotation: isMobile ? 45 : 90,
          font: {
            size: isMobile ? 8 : 10
          },
          color: darkMode ? '#e0e0e0' : '#666'
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        title: {
          display: true,
          text: t("graphs.periods"),
          font: {
            size: isMobile ? 10 : 12,
            weight: 'bold' 
          },
          color: darkMode ? '#e0e0e0' : '#333',
          padding: {top: 10}
        }
      },
      y: {
        title: {
          display: true,
          text: t("graphs.limitGeneration"),
          font: {
            size: isMobile ? 10 : 12,
            weight: 'bold' 
          },
          color: darkMode ? '#e0e0e0' : '#333'
        },
        ticks: {
          callback: function(value) {
            return typeof value === 'number' ? value.toFixed(1) : value;
          },
          color: darkMode ? '#e0e0e0' : '#666',
          precision: 1
        },
        grid: {
          color: darkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        },
        min: function(context) {
          const valores = context.chart.data.datasets[0].data.filter(v => v !== null);
          return valores.length > 0 ? Math.min(...valores) - 1 : 0;
        },
        max: function(context) {
          const valores = context.chart.data.datasets[0].data.filter(v => v !== null);
          return valores.length > 0 ? Math.max(...valores) + 1 : 100;
        }
      }
    }
  };

  const containerStyles = {
    padding: isMobile ? '8px' : '15px',
    gap: isMobile ? '8px' : '15px',
    border: darkMode ? '2px solid #bb86fc' : '2px solid #800080',
    borderRadius: '10px',
    backgroundColor: darkMode ? '#1e1e1e' : '#f9f9f9',
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    alignItems: 'flex-start',
    width: '100%',
    boxSizing: 'border-box'
  };

  const chartContainerStyles = {
    flex: isMobile ? '1 1 auto' : 5,
    minHeight: isMobile ? '40vh' : isTablet ? '50vh' : '62vh',
    width: '100%',
    backgroundColor: darkMode ? '#121212' : '#f5f5f5',
    padding: isMobile ? '5px' : '10px',
    borderRadius: '8px',
  };

  const sidebarStyles = {
    flex: isMobile ? '1 1 auto' : 1,
    minWidth: isMobile ? '100%' : '200px',
    border: darkMode ? '1px solid #333' : '1px solid #ddd',
    borderRadius: '8px',
    padding: isMobile ? '8px' : '15px',
    backgroundColor: darkMode ? '#121212' : 'white',
    maxHeight: isMobile ? 'auto' : '62vh',
    overflowY: 'auto',
    width: isMobile ? '100%' : undefined,
    marginTop: isMobile ? '10px' : 0
  };

  const buttonStyles = {
    margin: '5px',
    backgroundColor: darkMode ? '#bb86fc' : '#800080',
    color: '#fff',
    fontSize: isMobile ? '0.75rem' : '0.875rem',
    padding: isMobile ? '6px 12px' : '8px 16px',
    '&:hover': {
      backgroundColor: darkMode ? '#9a67ea' : '#6a0080',
    }
  };

  if (!tieneDatos) {
    return (
      <Paper
        sx={{
          width: '100%',
          padding: isMobile ? '15px' : '20px',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          border: '1px solid #551a8b',
          borderRadius: '20px',
          boxShadow: '7px 7px 20px rgba(0, 0, 0, 0.5)',
          marginBottom: isMobile ? '20px' : '50px'
        }}
      >
        <Typography variant={isMobile ? "subtitle1" : "h6"} color="textSecondary">
          {t('graphs.titleNAN')}
        </Typography>
        <Typography variant={isMobile ? "body2" : "body1"} color="textSecondary" sx={{ mt: 1 }}>
          {t('graphs.subtitle')}
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <Box style={containerStyles}>
        <Box style={chartContainerStyles}>
          <Line 
            data={prepararDatosGrafica(generadorSeleccionado)} 
            options={opcionesGrafica} 
          />
        </Box>
        
        <Box style={sidebarStyles}>
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ textAlign: 'center', mb: 2 }}>
            {t("graphs.Generadores")}
          </Typography>
          {generatorNames.map((generador) => (
            <Button
              key={generador}
              fullWidth
              variant={generadorSeleccionado === generador ? "contained" : "text"}
              onClick={() => setGeneradorSeleccionado(generador)}
              sx={{
                mb: 1,
                fontSize: isMobile ? '0.75rem' : '0.875rem',
                backgroundColor: generadorSeleccionado === generador 
                  ? (darkMode ? '#bb86fc' : '#800080') 
                  : 'transparent',
                color: generadorSeleccionado === generador 
                  ? '#fff' 
                  : (darkMode ? '#e0e0e0' : '#333'),
                '&:hover': {
                  backgroundColor: generadorSeleccionado === generador 
                    ? (darkMode ? '#9a67ea' : '#6a0080') 
                    : (darkMode ? '#333' : '#f0f0f0'),
                }
              }}
            >
              {generador.replace('g', '')}
            </Button>
          ))}
        </Box>
      </Box>

      <Box sx={{ 
        mt: 2, 
        display: 'flex', 
        justifyContent: 'center',
        flexWrap: isMobile ? 'wrap' : 'nowrap'
      }}>
        <Button 
          sx={buttonStyles} 
          onClick={() => setOrden('alfabetico')}
        >
          {t('graphs.OrderByname')}
        </Button>
        <Button 
          sx={buttonStyles} 
          onClick={() => setOrden('valor')}
        >
          {t('graphs.OrderByValue')}
        </Button>
      </Box>
    </>
  );
};

export default GraficaGeneradores;